import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { usd, shortDate, relDate } from "../lib/format";
import { Icon } from "../components/icons";
import { Avatar, Empty } from "../components/ui";
import type { RenewalOpportunity, ForecastCategory, RenewalStage } from "../lib/types";
import * as work from "../lib/workClient";

const TODAY = new Date("2026-08-17T00:00:00Z");
const CATS: { key: ForecastCategory; label: string; color: string }[] = [
  { key: "commit", label: "Commit", color: "var(--good)" },
  { key: "likely", label: "Likely", color: "var(--corona)" },
  { key: "at_risk", label: "At risk", color: "var(--watch)" },
  { key: "churn", label: "Churn", color: "var(--risk)" },
];
const CAT = Object.fromEntries(CATS.map((c) => [c.key, c]));
const STAGES: { key: RenewalStage; label: string }[] = [
  { key: "not_started", label: "Not started" }, { key: "in_progress", label: "In progress" },
  { key: "proposal_sent", label: "Proposal sent" }, { key: "closed_won", label: "Closed won" }, { key: "closed_lost", label: "Closed lost" },
];
const daysOut = (d: string) => Math.round((+new Date(d) - +TODAY) / 86400000);

export default function Renewals() {
  const { ws, userById } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [rows, setRows] = useState<RenewalOpportunity[]>(ws.renewals);
  const [window, setWindow] = useState<number>(90);

  useEffect(() => { work.loadRenewals(ws).then((r) => { setMode(r.mode); setRows(r.renewals); }); }, [ws]);

  const inWindow = useMemo(() => rows.filter((r) => { const d = daysOut(r.renewalDate); return d >= 0 && d <= window; }).sort((a, b) => +new Date(a.renewalDate) - +new Date(b.renewalDate)), [rows, window]);

  const f = useMemo(() => {
    const startingArr = inWindow.reduce((n, r) => n + r.arr, 0) || 1;
    const forecastRenewed = inWindow.reduce((n, r) => n + r.forecastAmount, 0);
    const expansion = inWindow.reduce((n, r) => n + r.expansionAmount, 0);
    const atRiskArr = inWindow.filter((r) => r.category === "at_risk" || r.category === "churn").reduce((n, r) => n + r.arr, 0);
    const byCat = CATS.map((c) => ({ ...c, arr: inWindow.filter((r) => r.category === c.key).reduce((n, r) => n + r.arr, 0), count: inWindow.filter((r) => r.category === c.key).length }));
    return { startingArr, forecastRenewed, expansion, atRiskArr, grr: Math.round((forecastRenewed / startingArr) * 100), nrr: Math.round(((forecastRenewed + expansion) / startingArr) * 100), byCat };
  }, [inWindow]);

  const patch = async (r: RenewalOpportunity, p: Partial<RenewalOpportunity>) => {
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...p } : x)));
    await work.updateRenewal(mode, ws, r.id, p);
  };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Renewals</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Forecast the book</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Every renewal ahead, with your call on where it lands. NRR and GRR roll up from the forecast.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <ModePill mode={mode} />
          <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
            <span className="faint">Window</span>
            <select className="inp inp--sm" value={window} onChange={(e) => setWindow(Number(e.target.value))}>
              <option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>1 year</option>
            </select>
          </label>
        </div>
      </div>

      <div className="stats">
        <Stat icon="target" label="Forecast renewed ARR" value={usd(f.forecastRenewed)} foot={`of ${usd(f.startingArr)} up for renewal`} />
        <Stat icon="refresh" label="GRR forecast" value={`${f.grr}%`} foot="Gross retention" color={f.grr >= 90 ? "var(--good)" : "var(--watch)"} />
        <Stat icon="pulse" label="NRR forecast" value={`${f.nrr}%`} foot="Incl. expansion" color={f.nrr >= 100 ? "var(--good)" : "var(--risk)"} />
        <Stat icon="bolt" label="At-risk ARR" value={usd(f.atRiskArr)} foot="At-risk + churn categories" color={f.atRiskArr ? "var(--risk)" : undefined} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__bd">
          <p className="eyebrow" style={{ margin: "0 0 12px" }}>Forecast by category · {inWindow.length} renewals in {window} days</p>
          <div className="forecast-bar">
            {f.byCat.filter((c) => c.arr > 0).map((c) => (
              <span key={c.key} className="forecast-seg" style={{ width: `${(c.arr / (f.startingArr || 1)) * 100}%`, background: c.color }} title={`${c.label}: ${usd(c.arr)}`} />
            ))}
          </div>
          <div className="row" style={{ gap: 18, marginTop: 12, flexWrap: "wrap" }}>
            {f.byCat.map((c) => (
              <span key={c.key} className="row" style={{ gap: 7, fontSize: 12.5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: c.color }} />
                <b>{c.label}</b> <span className="faint">{usd(c.arr)} · {c.count}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__hd"><div><h3>Renewal pipeline</h3><span className="sub">Set the category, forecast, and stage — the roll-up updates live</span></div></div>
        <div style={{ overflowX: "auto" }}>
          {inWindow.length === 0 ? <div className="card__bd"><Empty>No renewals in this window.</Empty></div> : (
            <table className="table">
              <thead><tr><th>Account</th><th>Renews</th><th>ARR</th><th>Category</th><th>Forecast</th><th>Expansion</th><th>Stage</th><th>Owner</th></tr></thead>
              <tbody>
                {inWindow.map((r) => {
                  const d = daysOut(r.renewalDate);
                  const owner = r.ownerId ? userById(r.ownerId) : undefined;
                  return (
                    <tr key={r.id}>
                      <td><Link to={`/accounts/${r.accountId}`} className="acct-cell"><Avatar name={r.accountName} size="sm" /><div><b>{r.accountName}</b></div></Link></td>
                      <td><div style={{ fontSize: 13 }}>{shortDate(r.renewalDate)}</div><small className={`faint ${d <= 30 ? "" : ""}`} style={{ color: d <= 30 ? "var(--risk)" : "var(--faint)" }}>{relDate(r.renewalDate)}</small></td>
                      <td className="num">{usd(r.arr)}</td>
                      <td>
                        <select className="cat-select" value={r.category} style={{ color: CAT[r.category].color, borderColor: CAT[r.category].color }}
                          onChange={(e) => { const category = e.target.value as ForecastCategory; patch(r, { category, forecastAmount: category === "commit" || category === "likely" ? r.arr : category === "at_risk" ? Math.round(r.arr * 0.6) : 0 }); }}>
                          {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                      </td>
                      <td><input className="num-input num" type="number" value={r.forecastAmount} onChange={(e) => patch(r, { forecastAmount: Number(e.target.value) })} /></td>
                      <td className="num faint">{r.expansionAmount ? "+" + usd(r.expansionAmount) : "—"}</td>
                      <td>
                        <select className="inp inp--sm" value={r.stage} onChange={(e) => patch(r, { stage: e.target.value as RenewalStage })}>
                          {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      </td>
                      <td>{owner ? <span className="row" style={{ gap: 7 }}><Avatar name={owner.name} size="sm" /><span style={{ fontSize: 12.5 }}>{owner.name}</span></span> : <span className="faint">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color, fontSize: 24 }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
